import { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, ArrowLeft, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white border border-zinc-200 rounded-sm px-4 py-3 text-sm text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-950 transition-colors duration-300";

const inr = (n) =>
  "Rs. " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function QuoteBuilder() {
  const { enquiryId } = useParams();
  const token = localStorage.getItem("sdc_admin_token");
  const [enquiry, setEnquiry] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientLocation, setClientLocation] = useState("");
  const [projectType, setProjectType] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [gstPercent, setGstPercent] = useState(18);
  const [notes, setNotes] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [terms, setTerms] = useState(
    "Payment Terms: 50% advance, 40% during execution, 10% on completion.\nThis quotation is valid for 15 days from the date of issue.\nFinal billing as per actual site measurements."
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Create Quote | Saurabh Decor Creations";
    if (!token) return;
    axios
      .get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const q = res.data.find((e) => e.id === enquiryId);
        if (q) {
          setEnquiry(q);
          setClientName(q.name);
          setClientPhone(q.phone);
          setClientLocation(q.project_location);
          setProjectType(q.project_type);
          setClientEmail(q.email);
        } else {
          toast.error("Enquiry not found.");
        }
      })
      .catch(() => toast.error("Could not load the enquiry."));
  }, [enquiryId, token]);

  if (!token) return <Navigate to="/admin" replace />;

  const updateItem = (i, field, value) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const gst = Math.round(subtotal * (parseFloat(gstPercent) || 0)) / 100;
  const total = subtotal + gst;

  const buildPayload = () => {
    const validItems = items
      .filter((it) => it.description.trim() && parseFloat(it.amount) > 0)
      .map((it) => ({ description: it.description.trim(), amount: parseFloat(it.amount) }));
    return {
      validItems,
      payload: {
        client_name: clientName.trim(),
        client_phone: clientPhone.trim(),
        client_email: clientEmail.trim(),
        client_location: clientLocation.trim(),
        project_type: projectType,
        items: validItems,
        gst_percent: parseFloat(gstPercent) || 0,
        notes: notes,
        terms: terms,
      },
    };
  };

  const download = async () => {
    const { validItems, payload } = buildPayload();
    if (!clientName.trim() || validItems.length === 0) {
      toast.error("Add a client name and at least one item with a description and amount.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/quotes/pdf`, payload, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `quotation-${clientName.trim().replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Quotation PDF downloaded.");
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const emailQuote = async () => {
    const { validItems, payload } = buildPayload();
    if (!clientName.trim() || !clientEmail.trim() || validItems.length === 0) {
      toast.error("Add a client name, client email and at least one priced item.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/quotes/email`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Quotation emailed to ${clientEmail.trim()}.`);
    } catch {
      toast.error("Could not email the quotation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="quote-builder-page" className="pt-[72px] min-h-screen bg-zinc-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <Link to="/admin" data-testid="quote-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Enquiries
        </Link>

        <div className="mt-6 mb-10">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 mb-3">Quotation</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-zinc-950">
            Create Quote{enquiry ? ` — ${enquiry.name}` : ""}
          </h1>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-8 lg:p-10 space-y-8">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Client Name</label>
              <input data-testid="quote-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Phone</label>
              <input data-testid="quote-client-phone" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Client Email</label>
              <input data-testid="quote-client-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Location</label>
              <input data-testid="quote-client-location" value={clientLocation} onChange={(e) => setClientLocation(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Project Type</label>
              <input data-testid="quote-project-type" value={projectType} onChange={(e) => setProjectType(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-medium text-zinc-950">Work Items</h2>
              <button
                data-testid="quote-add-item"
                onClick={() => setItems((prev) => [...prev, { description: "", amount: "" }])}
                className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-600 border border-zinc-200 rounded-sm px-4 py-2 hover:border-zinc-950 hover:text-zinc-950 transition-colors duration-300"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add Item
              </button>
            </div>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <input
                    data-testid={`quote-item-desc-${i}`}
                    value={it.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)}
                    placeholder="e.g. Modular kitchen — cabinets, shutters, hardware"
                    className={`${inputClass} flex-1`}
                  />
                  <input
                    data-testid={`quote-item-amount-${i}`}
                    type="number"
                    min="0"
                    value={it.amount}
                    onChange={(e) => updateItem(i, "amount", e.target.value)}
                    placeholder="Amount"
                    className={`${inputClass} w-36`}
                  />
                  {items.length > 1 && (
                    <button
                      data-testid={`quote-item-remove-${i}`}
                      onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-zinc-400 hover:text-red-700 transition-colors duration-300 p-1"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">GST %</label>
              <input data-testid="quote-gst-input" type="number" min="0" max="100" value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} className={`${inputClass} w-32`} />
            </div>
            <div className="sm:text-right space-y-1 text-sm text-zinc-600">
              <p>Subtotal: <span data-testid="quote-subtotal" className="font-semibold text-zinc-950">{inr(subtotal)}</span></p>
              <p>GST: <span data-testid="quote-gst-amount" className="font-semibold text-zinc-950">{inr(gst)}</span></p>
              <p className="font-heading text-xl font-medium text-zinc-950 pt-1">Grand Total: <span data-testid="quote-total">{inr(total)}</span></p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Notes (optional)</label>
            <textarea data-testid="quote-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, validity, scope notes..." className={`${inputClass} resize-none`} />
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Terms</label>
            <textarea data-testid="quote-terms-input" rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} className={`${inputClass} resize-none`} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <button
              data-testid="quote-download-btn"
              onClick={download}
              disabled={saving}
              className="bg-zinc-950 text-white text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-800 disabled:opacity-60 transition-colors duration-300 inline-flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <FileDown className="w-4 h-4" strokeWidth={1.5} />}
              Download PDF
            </button>
            <button
              data-testid="quote-email-btn"
              onClick={emailQuote}
              disabled={saving}
              className="border border-zinc-950 text-zinc-950 text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-950 hover:text-white disabled:opacity-60 transition-colors duration-300 inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              Email to Client
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
